import {css, Theme} from "@emotion/react";

import {Col, Row} from "@pg-design/grid-module";
import {calculateRemSize, flex, mh, mr, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {form, IFormProps} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {HOTLINE_INFO_PHONE_NUMBER} from "../../../hotline/constants/hotline_phone_numbers";
import {HotlineInfo} from "../../../search/components/HotlineInfo";
import {useAppSelector} from "../../../store/hooks";
import {InvestmentOfferSubCategory, investmentOfferTypes, InvestmentOfferTypeSlug} from "../../constants/investment_offer_constants";
import {IInvestmentOfferListFiltersFields} from "../../hooks/use_investment_offer_filters";
import {getInvestmentOffersSubCategoryOptions} from "../utils/get_investment_offers_subcategory_options";
import {InvestmentOfferFiltersDrawer} from "./InvestmentOfferFiltersDrawer";

interface IProps extends IFormProps<IInvestmentOfferListFiltersFields> {
    setInvestmentOffersBlendActive: (state: boolean) => void;
    isMap?: boolean;
    mapUrl?: string;
    listUrl?: string;
    isSticky?: boolean;
}

const CATEGORY_FILTER_NAME = "category";
const SUB_CATEGORY_FILTER_NAME = "subcategory";

export const InvestmentOfferListDesktopFilters = form((props: IProps) => {
    const {isSticky} = props;
    const {isMobile} = useUserDevice();

    const latestQuery = useAppSelector((state) => state.investmentOffer.list.offers.latestQuery);

    // category
    const categorySelectProps = props.getFieldProps(CATEGORY_FILTER_NAME);
    const categoryOptions = investmentOfferTypes.map((type) => ({value: type.slug, label: type.namePlural}));
    const onCategoryChange = (selectedCategory: unknown | null) => {
        const newCategory = selectedCategory as IOption<InvestmentOfferTypeSlug>;
        if (newCategory) {
            props.onChange({
                category: newCategory.value,
                subcategory: undefined // also reset subcategory
            });
            props.onAfterChange?.(CATEGORY_FILTER_NAME, newCategory.value);
            props.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, null);
        }
    };
    const categoryValue = categoryOptions.find(({value}) => value === categorySelectProps.value) || null;

    // subcategory
    const subCategorySelectProps = props.getFieldProps(SUB_CATEGORY_FILTER_NAME);
    const subCategoryOptions = getInvestmentOffersSubCategoryOptions(categorySelectProps.value);
    const onSubCategoryChange = (selectedSubCategory: unknown | null) => {
        const newSubCategory = selectedSubCategory as IOption<InvestmentOfferSubCategory>;
        subCategorySelectProps.onChange(SUB_CATEGORY_FILTER_NAME, newSubCategory?.value || null);
        subCategorySelectProps.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, newSubCategory?.value || null);
    };
    const subCategoryValue = subCategoryOptions.find(({value}) => value === subCategorySelectProps.value) || null;

    return (
        <Row css={searchSectionWrapper}>
            <Col lg={16} css={colWrapper}>
                {!isMobile && (
                    <Col lg={4} md={4} sm={4} css={buttonsColumnWrapper}>
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Col>
                )}
                {/* TODO: this whole component needs to be replaced with new searchDrawer, changed for Select due to hotfix */}
                <Col css={colPadding}>
                    <div css={selectWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INVESTMENT}>
                        <Select
                            {...subCategorySelectProps}
                            value={subCategoryValue}
                            options={subCategoryOptions}
                            onChange={onSubCategoryChange}
                            isClearable
                            isBorderless
                            placeholder={isMobile ? "Lokalizacja" : "Wybierz lokalizację"}
                            instanceId={SUB_CATEGORY_FILTER_NAME}
                            onMenuClose={() => !isMobile && props.setInvestmentOffersBlendActive(false)}
                            onMenuOpen={() => !isMobile && props.setInvestmentOffersBlendActive(true)}
                            css={locationSelectStyle}
                        />
                        <Select
                            {...categorySelectProps}
                            value={categoryValue}
                            onChange={onCategoryChange}
                            options={categoryOptions}
                            instanceId={CATEGORY_FILTER_NAME}
                            isBorderless
                            css={categorySelectStyle}
                        />
                    </div>
                </Col>
                <Col css={isSticky ? stickySpacing : pv(0)} lg={2} sm={1} md={2}>
                    <div css={filtersAndButtonsStyle}>
                        <InvestmentOfferFiltersDrawer
                            latestQuery={latestQuery}
                            isAbroad={categorySelectProps.value === InvestmentOfferTypeSlug.ABROAD}
                            mapUrl={props.mapUrl}
                            listUrl={props.listUrl}
                            isMap={props.isMap}
                        />
                    </div>
                </Col>
            </Col>
        </Row>
    );
});
const stickySpacing = (theme: Theme) => css`
    ${mt(0)};
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(0)}
    }
`;

const searchSectionWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${mh("auto")};
        position: relative;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colPadding = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(0)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
        ${ph(2)};
    }
`;

const filtersAndButtonsStyle = (theme: Theme) => css`
    ${flex()};
    gap: ${calculateRemSize(2)};
    justify-content: center;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${ph(0)};
        display: flex;
        justify-content: start;
        flex-wrap: wrap;
        row-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${theme.breakpoints.sm}) {
        column-gap: ${calculateRemSize(0)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: space-between;
    }
`;

const buttonsColumnWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(1)};
        ${ph(0)};
        ${mr(2)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        ${mr(9)};
    }
`;

const selectWrapper = () => css`
    display: flex;
    flex-direction: row;
    border-radius: 8px;
    background-color: white;
    border: solid var(--colors-gray-500) 1px;
`;

const locationSelectStyle = (theme: Theme) => css`
    border-right: var(--colors-gray-600) 1px solid;

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 70%;
    }
`;

const categorySelectStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        width: 30%;
    }
`;
import {css, Theme} from "@emotion/react";

import {Col, Row} from "@pg-design/grid-module";
import {calculateRemSize, flex, mh, mr, mt, ph, pt, pv} from "@pg-design/helpers-css";
import {IOption, Select} from "@pg-design/select-module";
import {form, IFormProps} from "@pg-mono/form";
import {NAVIGATION_TEST_IDS} from "@pg-mono/test-ids";
import {useUserDevice} from "@pg-mono/user-device";

import {HOTLINE_INFO_PHONE_NUMBER} from "../../../hotline/constants/hotline_phone_numbers";
import {HotlineInfo} from "../../../search/components/HotlineInfo";
import {useAppSelector} from "../../../store/hooks";
import {InvestmentOfferSubCategory, investmentOfferTypes, InvestmentOfferTypeSlug} from "../../constants/investment_offer_constants";
import {IInvestmentOfferListFiltersFields} from "../../hooks/use_investment_offer_filters";
import {getInvestmentOffersSubCategoryOptions} from "../utils/get_investment_offers_subcategory_options";
import {InvestmentOfferFiltersDrawer} from "./InvestmentOfferFiltersDrawer";

interface IProps extends IFormProps<IInvestmentOfferListFiltersFields> {
    setInvestmentOffersBlendActive: (state: boolean) => void;
    isMap?: boolean;
    mapUrl?: string;
    listUrl?: string;
    isSticky?: boolean;
}

const CATEGORY_FILTER_NAME = "category";
const SUB_CATEGORY_FILTER_NAME = "subcategory";

export const InvestmentOfferListDesktopFilters = form((props: IProps) => {
    const {isSticky} = props;
    const {isMobile} = useUserDevice();

    const latestQuery = useAppSelector((state) => state.investmentOffer.list.offers.latestQuery);

    // category
    const categorySelectProps = props.getFieldProps(CATEGORY_FILTER_NAME);
    const categoryOptions = investmentOfferTypes.map((type) => ({value: type.slug, label: type.namePlural}));
    const onCategoryChange = (selectedCategory: unknown | null) => {
        const newCategory = selectedCategory as IOption<InvestmentOfferTypeSlug>;
        if (newCategory) {
            props.onChange({
                category: newCategory.value,
                subcategory: undefined // also reset subcategory
            });
            props.onAfterChange?.(CATEGORY_FILTER_NAME, newCategory.value);
            props.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, null);
        }
    };
    const categoryValue = categoryOptions.find(({value}) => value === categorySelectProps.value) || null;

    // subcategory
    const subCategorySelectProps = props.getFieldProps(SUB_CATEGORY_FILTER_NAME);
    const subCategoryOptions = getInvestmentOffersSubCategoryOptions(categorySelectProps.value);
    const onSubCategoryChange = (selectedSubCategory: unknown | null) => {
        const newSubCategory = selectedSubCategory as IOption<InvestmentOfferSubCategory>;
        subCategorySelectProps.onChange(SUB_CATEGORY_FILTER_NAME, newSubCategory?.value || null);
        subCategorySelectProps.onAfterChange?.(SUB_CATEGORY_FILTER_NAME, newSubCategory?.value || null);
    };
    const subCategoryValue = subCategoryOptions.find(({value}) => value === subCategorySelectProps.value) || null;

    return (
        <Row css={searchSectionWrapper}>
            <Col lg={16} css={colWrapper}>
                {!isMobile && (
                    <Col lg={4} md={4} sm={4} css={buttonsColumnWrapper}>
                        <HotlineInfo hotlinePhoneNumber={HOTLINE_INFO_PHONE_NUMBER} />
                    </Col>
                )}
                {/* TODO: this whole component needs to be replaced with new searchDrawer, changed for Select due to hotfix */}
                <Col css={colPadding}>
                    <div css={selectWrapper} data-testid={NAVIGATION_TEST_IDS.SEARCH_BAR.INVESTMENT}>
                        <Select
                            {...subCategorySelectProps}
                            value={subCategoryValue}
                            options={subCategoryOptions}
                            onChange={onSubCategoryChange}
                            isClearable
                            isBorderless
                            placeholder={isMobile ? "Lokalizacja" : "Wybierz lokalizację"}
                            instanceId={SUB_CATEGORY_FILTER_NAME}
                            onMenuClose={() => !isMobile && props.setInvestmentOffersBlendActive(false)}
                            onMenuOpen={() => !isMobile && props.setInvestmentOffersBlendActive(true)}
                            css={locationSelectStyle}
                        />
                        <Select
                            {...categorySelectProps}
                            value={categoryValue}
                            onChange={onCategoryChange}
                            options={categoryOptions}
                            instanceId={CATEGORY_FILTER_NAME}
                            isBorderless
                            css={categorySelectStyle}
                        />
                    </div>
                </Col>
                <Col css={isSticky ? stickySpacing : pv(0)} lg={2} sm={1} md={2}>
                    <div css={filtersAndButtonsStyle}>
                        <InvestmentOfferFiltersDrawer
                            latestQuery={latestQuery}
                            isAbroad={categorySelectProps.value === InvestmentOfferTypeSlug.ABROAD}
                            mapUrl={props.mapUrl}
                            listUrl={props.listUrl}
                            isMap={props.isMap}
                        />
                    </div>
                </Col>
            </Col>
        </Row>
    );
});
const stickySpacing = (theme: Theme) => css`
    ${mt(0)};
    @media (min-width: ${theme.breakpoints.lg}) {
        ${mt(0)}
    }
`;

const searchSectionWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${mh("auto")};
        position: relative;
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colPadding = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(0)};
        ${ph(0)};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
        ${ph(2)};
    }
`;

const filtersAndButtonsStyle = (theme: Theme) => css`
    ${flex()};
    gap: ${calculateRemSize(2)};
    justify-content: center;

    @media (min-width: ${theme.breakpoints.md}) {
        ${pv(1)};
    }
`;

const colWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${ph(0)};
        display: flex;
        justify-content: start;
        flex-wrap: wrap;
        row-gap: ${calculateRemSize(2)};
    }

    @media (min-width: ${theme.breakpoints.sm}) {
        column-gap: ${calculateRemSize(0)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        justify-content: space-between;
    }
`;

const buttonsColumnWrapper = (theme: Theme) => css`
    @media (min-width: 0px) {
        ${pt(1)};
        ${ph(0)};
        ${mr(2)};
    }

    @media (min-width: ${theme.breakpoints.lg}) {
        ${mr(9)};
    }
`;

const selectWrapper = () => css`
    display: flex;
    flex-direction: row;
    border-radius: 8px;
    background-color: white;
    border: solid var(--colors-gray-500) 1px;
`;

const locationSelectStyle = (theme: Theme) => css`
    border-right: var(--colors-gray-600) 1px solid;

    @media (min-width: ${theme.breakpoints.sm}) {
        width: 70%;
    }
`;

const categorySelectStyle = (theme: Theme) => css`
    @media (min-width: ${theme.breakpoints.sm}) {
        width: 30%;
    }
`;
